// src/pages/ConversationDetailsPage.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import ConversationDetailsView from '@/components/ConversationDetails/ConversationDetailsView';
import type { ApiConfig, AppConfig } from '@/components/ConversationItem/types';
import { authStorage } from '@/lib/storage';
import { dashboardService } from '@/services/dashboardService';
import { apiService } from '@/services/api';
import { config as envConfig } from '@/config/env';
import { InfoWindowProvider } from '@/contexts/InfoWindowContext';
import EditCard from '@/components/EditCard/EditCard';
import type { EditCardRef } from '@/components/EditCard/EditCard';
import { socketService } from '@/services/socketService';
import type { ShowRefreshButtonPayload } from '@/types/socketRefresh';
import { RefreshNotificationButton } from '@/components/Dashboard/RefreshNotificationButton';

/**
 * Standalone ConversationDetails page.
 * Route: /Dashboard/ConversationDetails/:conversationId
 *
 * This page does NOT use MainLayout, so it must independently
 * fetch essential data that MainLayout normally provides.
 */
export const ConversationDetailsPage: React.FC = () =>
{
  const navigate = useNavigate();
  const socketConnectionId = useSocketConnectionId();

  // Extract conversationId from hash: /#/193370
  const hash = window.location.hash; // e.g. "#/193370"
  const conversationId = parseInt(hash.replace('#/', '').replace('#', '') || '0', 10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasItemCreationPermission, setHasItemCreationPermission] = useState(false);
  const [conversationTitle, setConversationTitle] = useState<string>('');
  const [forceReload, setForceReload] = useState(0);
  const [hasPendingUpdate, setHasPendingUpdate] = useState(false);

  useEffect(() =>
  {
    if (conversationTitle)
    {
      document.title = conversationTitle;
    }
    return () =>
    {
      document.title = 'Corgee';
    };
  }, [conversationTitle]);

  // Listen for socket updates and show the refresh button when relevant
  useEffect(() =>
  {
    return socketService.on(
      'socket:client:ShowRefreshButton',
      (payload: ShowRefreshButtonPayload) =>
      {
        if (
          payload.SocketConnectionId &&
          payload.SocketConnectionId !== '' &&
          payload.SocketConnectionId === socketConnectionId
        )
        {
          return;
        }

        if (
          payload.conversationId === conversationId ||
          (payload.conversationId == null && conversationId > 0)
        )
        {
          setHasPendingUpdate(true);
        }
      },
    );
  }, [conversationId, socketConnectionId]);

  const editCardRef = useRef<EditCardRef>(null);
  const editCardInitRef = useRef<((conv: any, itemType: any) => void) | null>(null);
  const reloadConversationRef = useRef<((id: number) => void) | null>(null);

  // API config from session
  const api = useMemo((): ApiConfig & {
    refreshToken?: string;
    ExternalData?: string;
    SocketServer?: string;
    TenancyName: string;
    PersonId: string;
  } =>
  {
    const token = authStorage.getAccessToken() || '';
    const personId = authStorage.getPersonId() || '0';

    return {
      url: envConfig.api.baseUrl,
      token,
      refreshToken: '',
      ExternalData: '',
      SocketServer: envConfig.socket.server,
      TenancyName: envConfig.tenant.defaultName,
      PersonId: personId || '0',
    };
  }, []);

  // App config
  const config = useMemo((): AppConfig & {
    buildVersion?: string;
    webChaturl?: string;
    appPrefix?: string;
  } =>
  {
    return {
      weburl: envConfig.web.url,
      apiurl: envConfig.api.baseUrl,
      buildVersion: '1.0.0',
      webChaturl: envConfig.web.url + '/CHAT',
      appPrefix: '',
    };
  }, []);

  // Storage data helper
  const getStorageData = () =>
  {
    let data: Record<string, any> = {};
    try
    {
      data = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
    }
    catch
    {
      /* ignore */
    }

    let allLayers: any[] = [];
    try
    {
      allLayers = JSON.parse(sessionStorage.getItem('C2_AllLayers') || '[]');
    }
    catch
    {
      /* ignore */
    }

    let layersToHide: any[] = [];
    try
    {
      layersToHide = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
    }
    catch
    {
      /* ignore */
    }

    return {
      data,
      allLayers,
      layersToHide,
      layerId: parseInt(localStorage.getItem('C2_layerid') || '0'),
      conv_maxAllowedUpload: 20480000,
      enableChat: localStorage.getItem('C2_enableChat') === 'true',
      personid: parseInt(localStorage.getItem('C2_personid') || '0'),
    };
  };

  // User data helper
  const getUserData = () =>
  {
    const personName =
      sessionStorage.getItem('C2_personName') || authStorage.getGivenName() || 'User';
    const imageProfile = sessionStorage.getItem('C2_profileImage') || 'Images/user.png';
    const personId = parseInt(authStorage.getPersonId() || '0', 10);
    return { imageProfile, personName, PersonId: personId, UserName: personName };
  };

  // Menu configuration
  const getConfigMenus = () => ({
    conversation: [
      'goto-project',
      'close-infowindow',
      'opendetails-newtab',
      'mark-redflag',
      'mark-important',
      'follow-conv',
      'create-itemtype',
      'convert-to',
      'duplicate-project',
      'copylink-to-clipboard',
      'move-to',
      'copy-to',
      'submit-time',
      'edit-history',
      'edit',
      'delete',
      'duplicate-item',
      'archive',
    ],
    reply: ['mark-important', 'mark-favorite', 'repost', 'edit', 'delete', 'create-itemtype'],
  });

  // Real item types
  const realItemTypes = {
    Users: { Name: 'People', Icon: 'icons-menu-account-box' },
    Tasks: { Name: 'Tasks', Icon: 'icons-menu-epic' },
  };

  // Initialize essential data on mount (replaces MainLayout data loading)
  useEffect(() =>
  {
    const initializeData = async () =>
    {
      try
      {
        setLoading(true);
        setError(null);

        const [peopleData, itemCreationPerm, _layersData, profileData] = await Promise.all([
          dashboardService.getPeopleData().catch(() => null),
          apiService.get<boolean>('Security/HasItemCreationPermission').catch(() => false),
          dashboardService.getPersonLayers().catch(() => []),
          dashboardService.getUserProfile().catch(() => null),
        ]);

        // Store people data in sessionStorage (same as MainLayout)
        if (peopleData)
        {
          const dataToStore = {
            ...peopleData,
            PTOApprover: !!peopleData.PTOApprover,
            HRApprover: !!peopleData.HRApprover,
          };
          sessionStorage.setItem('C2_data', JSON.stringify(dataToStore));
          localStorage.setItem('C2_HRApprover', String(dataToStore.HRApprover));
          localStorage.setItem('C2_PTOApprover', String(dataToStore.PTOApprover));
        }

        // Store item creation permission
        localStorage.setItem('C2_hasItemCreationPermission', String(!!itemCreationPerm));
        setHasItemCreationPermission(!!itemCreationPerm);

        // Store profile data
        if (profileData)
        {
          sessionStorage.setItem('C2_profileImage', profileData.ImageName || '');
          sessionStorage.setItem('C2_personName', profileData.PersonName || '');
        }
      }
      catch (err)
      {
        console.error('Error initializing ConversationDetailsPage:', err);
        setError('Error loading page data');
      }
      finally
      {
        setLoading(false);
      }
    };

    if (conversationId > 0)
    {
      initializeData();
    }
    else
    {
      setError('Invalid Conversation ID');
      setLoading(false);
    }
  }, [conversationId]);

  // Build request object for ConversationDetailsView
  const buildRequest = () => ({
    api,
    enableLoading: false,
    showErrorMessage: true,
    isInfoWindow: false,
    isInfowindowViewCard: false,
    hasArchivedFilter: false,
    isConversationDetailsView: true,
    config,
    userData: getUserData(),
    storageData: getStorageData(),
    params: {
      ConversationId: conversationId,
      SocketConnectionId: socketConnectionId || socketService.connectionId || undefined,
      IsFromProject: false,
      ConfigMenus: getConfigMenus(),
      ShowRefreshButton: true,
      ForceReload: forceReload,
    },
    HasItemCreationPermission: hasItemCreationPermission,
    functions: {
      onLoadDataCallback: (conversation: any) =>
      {
        if (conversation?.Title)
        {
          setConversationTitle(conversation.Title);
        }
      },
      onSendError: (_convId: number) => {},
      loadingBarStart: () => {},
      loadingBarComplete: () => {},
      // Standalone page has no info window: open the linked conversation in a new tab
      openInfoWindow: (conv: any) =>
      {
        const id = typeof conv === 'number' ? conv : conv?.ConversationId;
        if (!id) return;
        window.open(
          window.location.origin + import.meta.env.BASE_URL + 'Dashboard/ConversationDetails/#/' + id,
          '_blank',
        );
      },
      // Standalone page has no info window: open the parent card in a new window
      onClickParentTasks: (_e: any, parentConversationId: number, closeModalCb?: () => void) =>
      {
        if (typeof closeModalCb === 'function')
        {
          closeModalCb();
        }
        if (!parentConversationId) return;
        window.open(
          window.location.origin +
            import.meta.env.BASE_URL +
            'Dashboard/ConversationDetails/#/' +
            parentConversationId,
          '_blank',
        );
      },
      attachReactFunctions: (reactFunctions: any) =>
      {
        if (reactFunctions?.onReloadConversation)
        {
          reloadConversationRef.current = reactFunctions.onReloadConversation;
        }
      },
      onUpdateConversation: (
        _convId: number,
        _newAttrs: any,
        _convObj: any,
        _renderType: any,
      ) => {},
      onAddReply: (_convId: number, _reply: any, _convObj: any) => {},
      onUpdateReply: (
        _convObj: any,
        _replyUpdated: any,
        _newAttrs: any,
        _newTotalFavorites: any,
      ) => {},
      addLayerToCard: (_e: any, _conversation: any, _layer: any, _callback: any) => {},
      getTaskUsers: (_e: any, _convId: number) => {},
      getTaskDashboards: (_e: any, _convId: number) => {},
      getTaskParent: (_e: any, _convId: number) => {},
      openFavoritesModal: (_e: any, _replyId: number) => {},
      goToProject: (_e: any, _convProject: any) =>
      {
        const path = `/ProjectDetails/${_convProject.ProjectId}`;
        if (_e && (_e.shiftKey || _e.ctrlKey || _e.metaKey))
        {
          window.open(window.location.origin + path, '_blank');
        }
        else
        {
          navigate(path);
        }
      },
      goToDashboard: () =>
      {
        navigate('/');
      },
      editConversation: (_e: any, _currentConv: any) => {},
      deleteConversation: (_e: any, _currentConv: any) => {},
      onOpenGallery: (_e: any, _attachments: any) => {},
      onFileDownload: (_e: any, _attachment: any) => {},
      onCopyToClipboard: (_convId: number, _text: string, _result: any) => {},
      editReply: (_e: any, _currentReply: any, _currentConv: any) => {},
      deleteReply: (_e: any, _reply: any, _conversation: any) => {},
      archiveConversation: (_e: any, _conversation: any, callback: any) =>
      {
        if (callback) callback();
      },
      destroy: () => {},
      saveSearchResult: (_newDashboard: any) => {},
      copyTo: (_e: any, _conversation: any) => {},
      onReplyDeleted: (_e: any, _replyDeleted: any, _totalReplies: number) => {},
      onCreateItemFromCustomType: (item: any, conversation: any) =>
      {
        const itemType = {
          ...item,
          ConfigValueId: item.ConfigValueID,
          RelatedReplyId: 0,
        };
        const conv = {
          ProjectId: conversation?.ProjectId || 0,
          ConversationId: conversation?.ConversationId,
          DashboardGroupId: conversation?.DashboardGroupId,
        };
        editCardInitRef.current?.(conv, itemType);
        editCardRef.current?.openModal();
      },
      onItemConvertTo: (_conversation: any) => {},
      onChangedProject: (_e: any, _conversation: any) => {},
      onChangedSecurityGroups: (_e: any, _conversation: any) => {},
      onChangedGroupLayers: (_e: any, _conversation: any) => {},
      onChangeLayers: (_conversation: any, _isFromInfowindow?: boolean) => {},
      onCloseInfoWindow: () => {},
      getReloadItemFn: (_updateItemFn: any, _conversationid: number) => {},
      attachSocket: (_SocketConnectionId: string) => {},
      onDuplicateItem: (_e: any, _conversation: any) => {},
    },
    realItemTypes,
  });

  // Loading state
  if (loading)
  {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            className="animate-spin"
            style={{
              width: '48px',
              height: '48px',
              border: '3px solid #e0e0e0',
              borderTopColor: '#5dade2',
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error)
  {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <div
          style={{
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textAlign: 'center',
          }}
        >
          <p style={{ color: '#c62828', fontSize: '16px', marginBottom: '16px' }}>{error}</p>
          <a
            href="/"
            onClick={e =>
            {
              e.preventDefault();
              navigate('/');
            }}
            style={{ color: '#d4770c', textDecoration: 'none', fontSize: '14px' }}
          >
            Back to Corgee
          </a>
        </div>
      </div>
    );
  }

  return (
    <InfoWindowProvider
      api={api}
      config={config}
      storageData={getStorageData()}
      userData={getUserData()}
      realItemTypes={realItemTypes}
      functionsLoad={{}}
    >
      <RefreshNotificationButton
        hasPendingUpdates={hasPendingUpdate}
        message="This item has changed, click here to refresh"
        onClick={() =>
        {
          setHasPendingUpdate(false);
          setForceReload(prev => prev + 1);
        }}
        style={{ top: '1.5rem' }}
      />
      <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Conversation Details */}
        <div style={{ maxWidth: '880px', margin: '0 auto', padding: '0 20px 20px' }}>
          {/* Back to Corgee link */}
          <div style={{ padding: '12px 0', textAlign: 'left' }}>
            <a
              href="/"
              onClick={e =>
              {
                e.preventDefault();
                navigate('/');
              }}
              style={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#23527c',
                letterSpacing: 0,
                textDecoration: 'none',
              }}
              onMouseEnter={e =>
              {
                const el = e.target as HTMLElement;
                el.style.textDecoration = 'underline';
                el.style.color = '#23527c';
              }}
              onMouseLeave={e =>
              {
                const el = e.target as HTMLElement;
                el.style.textDecoration = 'none';
                el.style.color = '#23527c';
              }}
            >
              Back to Corgee
            </a>
          </div>

          <ConversationDetailsView request={buildRequest()} />
        </div>
      </div>
      <EditCard
        ref={editCardRef}
        config={{
          apiUrl: api.url,
          authToken: api.token,
          SocketConnectionId: socketConnectionId,
          attachReactFunctions: (fns: any) =>
          {
            editCardInitRef.current = fns.initConfig;
          },
          functions: {
            loadingBarStart: () => {},
            loadingBarComplete: () => {},
            conversationResponse: () => {},
          },
          params: { Conversation: {} },
        }}
        UpdateCustomFields={() => {}}
      />
    </InfoWindowProvider>
  );
};

export default ConversationDetailsPage;
